// ============================================================================
// FNE-BRIDGE - Store Connexions BDD Externes (Zustand)
// ============================================================================

import { create } from 'zustand';
import type { DatabaseConnection, MappingProfile, MappingRule } from '../types/models';
import type { TableInfo, TestConnectionResponse, TestMappingResponse } from '../types/api';

// ============================================================================
// Types du state
// ============================================================================

interface ConnectionState {
  // --- Liste connexions ---
  connections: DatabaseConnection[];
  connectionsLoading: boolean;
  connectionsError: string | null;

  // --- Connexion courante ---
  currentConnection: DatabaseConnection | null;
  currentConnectionTables: TableInfo[];
  testResult: TestConnectionResponse | null;
  testLoading: boolean;

  // --- Profils de mapping ---
  mappingProfiles: MappingProfile[];
  mappingProfilesLoading: boolean;
  currentProfile: MappingProfile | null;
  currentProfileRules: MappingRule[];

  // --- Test mapping ---
  mappingTestResult: TestMappingResponse | null;
  mappingTestLoading: boolean;

  // --- Champs cibles FNE (constante) ---
  fneTargetFields: FneTargetField[];

  // --- Actions connexions ---
  setConnections: (connections: DatabaseConnection[]) => void;
  setConnectionsLoading: (loading: boolean) => void;
  setConnectionsError: (error: string | null) => void;
  setCurrentConnection: (connection: DatabaseConnection | null) => void;
  setCurrentConnectionTables: (tables: TableInfo[]) => void;
  addConnection: (connection: DatabaseConnection) => void;
  updateConnection: (id: string, updates: Partial<DatabaseConnection>) => void;
  removeConnection: (id: string) => void;
  setTestResult: (result: TestConnectionResponse | null) => void;
  setTestLoading: (loading: boolean) => void;

  // --- Actions mapping ---
  setMappingProfiles: (profiles: MappingProfile[]) => void;
  setMappingProfilesLoading: (loading: boolean) => void;
  setCurrentProfile: (profile: MappingProfile | null, rules?: MappingRule[]) => void;
  addMappingRule: (rule: MappingRule) => void;
  updateMappingRule: (id: string, updates: Partial<MappingRule>) => void;
  removeMappingRule: (id: string) => void;
  reorderMappingRules: (rules: MappingRule[]) => void;
  setMappingTestResult: (result: TestMappingResponse | null) => void;
  setMappingTestLoading: (loading: boolean) => void;

  // --- Reset ---
  reset: () => void;
}

// ============================================================================
// Champs cibles FNE (référence pour le mapping)
// ============================================================================

export interface FneTargetField {
  name: string;
  label: string;
  required: boolean;
  type: 'string' | 'number' | 'date' | 'boolean';
  description: string;
}

export const FNE_TARGET_FIELDS: FneTargetField[] = [
  { name: 'clientNcc', label: 'NCC Client', required: false, type: 'string', description: 'Numéro Compte Contribuable du client' },
  { name: 'clientCompanyName', label: 'Nom Client', required: true, type: 'string', description: 'Raison sociale ou nom du client' },
  { name: 'clientPhone', label: 'Téléphone Client', required: false, type: 'string', description: 'Numéro de téléphone du client' },
  { name: 'clientEmail', label: 'Email Client', required: false, type: 'string', description: 'Adresse email du client' },
  { name: 'clientSellerName', label: 'Nom Vendeur', required: false, type: 'string', description: 'Nom du vendeur/commercial' },
  { name: 'invoiceNumber', label: 'N° Facture', required: true, type: 'string', description: 'Numéro de facture source' },
  { name: 'invoiceDate', label: 'Date Facture', required: true, type: 'date', description: 'Date de la facture' },
  { name: 'dueDate', label: 'Date Échéance', required: false, type: 'date', description: 'Date d\'échéance du paiement' },
  { name: 'totalHT', label: 'Total HT', required: true, type: 'number', description: 'Montant total hors taxes' },
  { name: 'totalTVA', label: 'Total TVA', required: false, type: 'number', description: 'Montant total de la TVA' },
  { name: 'totalTTC', label: 'Total TTC', required: true, type: 'number', description: 'Montant total toutes taxes comprises' },
  { name: 'itemReference', label: 'Réf. Article', required: false, type: 'string', description: 'Référence de l\'article' },
  { name: 'itemDescription', label: 'Désignation', required: true, type: 'string', description: 'Désignation de l\'article' },
  { name: 'itemQuantity', label: 'Quantité', required: true, type: 'number', description: 'Quantité de l\'article' },
  { name: 'itemUnitPrice', label: 'Prix Unitaire HT', required: true, type: 'number', description: 'Prix unitaire hors taxes' },
  { name: 'itemDiscount', label: 'Remise %', required: false, type: 'number', description: 'Pourcentage de remise sur l\'article' },
  { name: 'itemVatRate', label: 'Taux TVA', required: true, type: 'number', description: 'Taux de TVA (18, 9 ou 0)' },
  { name: 'itemUnit', label: 'Unité', required: false, type: 'string', description: 'Unité de mesure' },
];

// ============================================================================
// Store
// ============================================================================

export const useConnectionStore = create<ConnectionState>()((set) => ({
  connections: [],
  connectionsLoading: false,
  connectionsError: null,
  currentConnection: null,
  currentConnectionTables: [],
  testResult: null,
  testLoading: false,
  mappingProfiles: [],
  mappingProfilesLoading: false,
  currentProfile: null,
  currentProfileRules: [],
  mappingTestResult: null,
  mappingTestLoading: false,
  fneTargetFields: FNE_TARGET_FIELDS,

  // --- Actions connexions ---
  setConnections: (connections) => set({ connections, connectionsLoading: false }),
  setConnectionsLoading: (connectionsLoading) => set({ connectionsLoading }),
  setConnectionsError: (connectionsError) => set({ connectionsError, connectionsLoading: false }),

  setCurrentConnection: (currentConnection) =>
    set({ currentConnection, currentConnectionTables: [], testResult: null }),

  setCurrentConnectionTables: (currentConnectionTables) => set({ currentConnectionTables }),

  addConnection: (connection) =>
    set((state) => ({ connections: [...state.connections, connection] })),

  updateConnection: (id, updates) =>
    set((state) => ({
      connections: state.connections.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      currentConnection:
        state.currentConnection?.id === id
          ? { ...state.currentConnection, ...updates }
          : state.currentConnection,
    })),

  removeConnection: (id) =>
    set((state) => ({
      connections: state.connections.filter((c) => c.id !== id),
      currentConnection: state.currentConnection?.id === id ? null : state.currentConnection,
    })),

  setTestResult: (testResult) => set({ testResult, testLoading: false }),
  setTestLoading: (testLoading) => set({ testLoading, testResult: null }),

  // --- Actions mapping ---
  setMappingProfiles: (mappingProfiles) => set({ mappingProfiles, mappingProfilesLoading: false }),
  setMappingProfilesLoading: (mappingProfilesLoading) => set({ mappingProfilesLoading }),

  setCurrentProfile: (currentProfile, currentProfileRules = []) =>
    set({ currentProfile, currentProfileRules }),

  addMappingRule: (rule) =>
    set((state) => ({ currentProfileRules: [...state.currentProfileRules, rule] })),

  updateMappingRule: (id, updates) =>
    set((state) => ({
      currentProfileRules: state.currentProfileRules.map((r) =>
        r.id === id ? { ...r, ...updates } : r
      ),
    })),

  removeMappingRule: (id) =>
    set((state) => ({
      currentProfileRules: state.currentProfileRules.filter((r) => r.id !== id),
    })),

  reorderMappingRules: (currentProfileRules) => set({ currentProfileRules }),

  setMappingTestResult: (mappingTestResult) => set({ mappingTestResult, mappingTestLoading: false }),
  setMappingTestLoading: (mappingTestLoading) => set({ mappingTestLoading, mappingTestResult: null }),

  reset: () =>
    set({
      connections: [],
      connectionsLoading: false,
      connectionsError: null,
      currentConnection: null,
      currentConnectionTables: [],
      testResult: null,
      testLoading: false,
      mappingProfiles: [],
      mappingProfilesLoading: false,
      currentProfile: null,
      currentProfileRules: [],
      mappingTestResult: null,
      mappingTestLoading: false,
    }),
}));
